import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { Observable, ReplaySubject } from 'rxjs';

import { Router } from "@angular/router";
import { AppConfigService } from '../app-config/app-config.service';
import { IAccessToken, IServerToken } from './auth.model';

@Injectable({
  providedIn: "root"
})
export class AuthService {
    private token: IAccessToken = Object.assign({});
    private isAuthenticated: boolean = false;
    private currUserName: string;
    private userPermissions;

    constructor(private http: HttpClient, @Inject(AppConfigService) private _appConfigService: AppConfigService, private router: Router) {
    }

    getCachedToken(): IAccessToken {
        return this.token;
    }

    isPermmitted(permissionName: string): boolean {
        if (this.userPermissions)
            return this.userPermissions[permissionName];
        else
            return false;
    }

    getCurrUserName(): string {
        return this.currUserName;
    }

    handleToken(token: IAccessToken): Observable<string>{
        const tokenSubject = new ReplaySubject<string>(1);
        const parsedToken = this.parseJwt(token.accessToken);
        this.currUserName = parsedToken.unique_name;

        // Set the token
        this.token = token;

        this.getUserPermissions().subscribe(
            data => {
                this.userPermissions = data;
                tokenSubject.next("success");
            }, error => {
                tokenSubject.error(error);
            });
        return tokenSubject.asObservable();
    }

    public parseJwt(token) {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace('-', '+').replace('_', '/');
        return JSON.parse(window.atob(base64));
    }

    public getIsAuthenticated(): boolean {
        if (this.isAuthenticated) {
            return true;
        }
        else {
            return false;
        }
    }

    public logout() {

        this.invalidateRefreshToken();
        this.token = Object.assign({});
        this.isAuthenticated = false;
        this.currUserName = null;
    }

    private getUserPermissions(): Observable<string> {
        return this.http.get<string>(this._appConfigService.getWebapiURL() + "/ApplicationUser/GetUserPermissions");
    }

    public validateExternalToken(): Observable<string>{
        const result = new ReplaySubject<string>(1);
        this.http.get<boolean>(this._appConfigService.getWebapiURL() + "/HealthCheck/ValidateToken").subscribe(res=>{
                this.isAuthenticated = true;

                // Set the refresh token to execute before token is expired
                this.setRefreshToken(this.safeExpiration(this.token.expiresIn));
                result.next("success");
        },err=>{
            result.error(err);
        });
        return result.asObservable();
    }


    public login(data : IAccessToken): Observable<string> {
        const tokenSubject = new ReplaySubject<string>(1);

        this.isAuthenticated = true;

        this.handleToken(data).subscribe(res => {
                tokenSubject.next("success");
            });

        // Set the refresh token to execute before token is expired
        this.setRefreshToken(this.safeExpiration(data.expiresIn));

        return tokenSubject.asObservable();
    }

    public refreshAccessToken(): Observable<string> {
        const tokenSubject = new ReplaySubject<string>(1);
        // Server token request
        this.getTokenByRefresh().subscribe(
            // Authentication success
            data => {
                // Set the token
                this.token.accessToken = data.access_token;
                this.token.expiresIn = data.expires_in;
                this.token.refreshToken = data.refresh_token;

                this.isAuthenticated = true;
                tokenSubject.next("success");

            },
            // Authentication error
            err => {
                // If an external token has expired (originating application caused it to expire), logout
                if (this.token.isExternalToken
                    && err.error && err.error.error === 'invalid_grant') {
                    this.logout();
                    this.router.navigate(['/login']);
                } else {
                    this.tokenRefreshRetry(tokenSubject, err);
                }

            });
        return tokenSubject.asObservable();
    }

    public tokenRefreshRetry(subject: ReplaySubject<string>, lastError: any) {
        if (confirm("Failed to refresh user authentication token. Would you like to retry?")) { // if we need to retry or continue
            // Try to get token with oauth2
            // Server token request
            this.getTokenByRefresh().subscribe(
                data => {
                    // Set the token
                    this.token.accessToken = data.access_token;
                    this.token.expiresIn = data.expires_in;
                    this.token.refreshToken = data.refresh_token;

                    this.isAuthenticated = true;
                    subject.next("success");
                },
                err => {
                    this.tokenRefreshRetry(subject, err);
                });
        } else {
            subject.error(lastError);
        }
    }


    public getTokenByRefresh(): Observable<IServerToken> {
        const xmlRequestParam =
            encodeURIComponent("refresh_token") + '=' + encodeURIComponent(this.token.refreshToken) + '&' +
            encodeURIComponent("grant_type") + '=' + encodeURIComponent("refresh_token");
        const headers = {
            headers: new HttpHeaders(
                {
                    ["Content-Type"]: 'application/x-www-form-urlencoded;charset=utf-8'
                })
        };

        return this.http.post<IServerToken>(this._appConfigService.getAuthURL() + "/OAuth/Token", xmlRequestParam, headers);
    }

  public changePassword(userName: string, oldPassword: string, newPassword: string): Observable<any> {

    const params = `userName=${encodeURIComponent(userName)}` +
      `&oldPassword=${encodeURIComponent(oldPassword)}&newPassword=${encodeURIComponent(newPassword)}`;
    const reqSpec = {
      headers: new HttpHeaders(
        {
          ["Content-Type"]: 'application/x-www-form-urlencoded;charset=utf-8'
        })
    };
    return this.http.post(this._appConfigService.getAuthURL()+ "/api/ChangePassword",  params,reqSpec);
  }


    private setRefreshToken(refreshInterval : number):void {
        setTimeout(() => {
            this.refreshAccessToken()
                .subscribe(
                    refreshResult => {
                        const newRefreshInterval = this.safeExpiration(this.token.expiresIn);
                        // set the next refresh
                        this.setRefreshToken(newRefreshInterval);
                    }, (badReply) => {
                        // if failed to get a new Token retry every 2 seconds
                        this.setRefreshToken(2);
                    });
        }, refreshInterval * 1000);
    }

    private safeExpiration(expiration:number){
        return Math.round(expiration/2);
    }

    private invalidateRefreshToken() {

        // We don't want to invalidate external tokens since they might be still in use in the originating application
        if (this.token.refreshToken && !this.token.isExternalToken) {
            const refreshToken = this.token.refreshToken;
            this.http.post(this._appConfigService.getAuthURL() + "/OAuth/InvalidateRefreshToken", {refreshToken} )
                .subscribe((result) => {
                        console.log("Invalidated refresh token successfully")
                }, (error)=> {
                    console.error("Failed to invalidate refresh token", error)
                })
        }
    }
}

